import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Row, Col, Card, Table, Form, Button, Spinner, Alert, Pagination } from 'react-bootstrap';
import { useCatalogue } from '../hooks/useCatalogue';
import AppIcon from './AppIcon';
import AddMutualFundToPortfolioModal from './AddMutualFundToPortfolioModal';

interface MutualFund {
  id: number;
  stockid: number;
  title: string;
  body: string;
  isin: string;
}

const MutualFunds: React.FC = () => {
  const { items: funds, loading, error, searchTerm, setSearchTerm, currentPage, totalPages, totalCount, search, changePage, retry } = useCatalogue<MutualFund>('mutual-funds');
  const [fundToAdd, setFundToAdd] = useState<MutualFund | null>(null);
  const [addSuccess, setAddSuccess] = useState('');

  const handleSearch = (event: React.FormEvent) => { event.preventDefault(); search(); };
  const handlePageChange = changePage;

  const handleAddToPortfolio = (fund: MutualFund) => {
    setAddSuccess('');
    setFundToAdd(fund);
  };

  return (
    <div className="directory-page">
      <header className="directory-hero directory-hero--funds">
        <div className="directory-hero__icon"><AppIcon name="funds" /></div>
        <div>
          <span className="page-eyebrow">Investment catalogue</span>
          <h1>Mutual Funds</h1>
          <p>Search AMFI-listed schemes, review NAV history, and connect the right fund to your portfolio.</p>
        </div>
      </header>

      {addSuccess && (
        <Alert variant="success" dismissible onClose={() => setAddSuccess('')}>
          {addSuccess}
        </Alert>
      )}

      {error && <Alert variant="warning">{error} <Button size="sm" variant="outline-dark" onClick={retry}>Retry</Button></Alert>}
      {/* Search */}
      <Card className="directory-search-card mb-4">
        <Card.Body>
          <p className="workspace-status">Results update as you type. Search by name or identifier.</p>
          <Form onSubmit={handleSearch}>
            <Row className="align-items-end g-3">
              <Col md={8}>
                <Form.Group controlId="mutualfunds-search">
                  <Form.Label>Search the mutual-fund catalogue</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Search by fund name or ISIN..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={4} className="d-grid">
                <Button type="submit" variant="primary" className="directory-search-button" disabled={loading}>
                  {loading ? 'Searching…' : 'Search now'}
                </Button>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>

      {/* Results */}
      <Card className="directory-results-card">
        <Card.Header className="directory-results-card__header">
          <div><span className="page-eyebrow">Search results</span><h2>Available mutual funds</h2></div>
          {!loading && <span className="directory-results-card__count">{totalCount} results</span>}
        </Card.Header>
        <Card.Body className="p-0">
          {loading && funds.length === 0 ? (
            <div className="d-flex justify-content-center p-4">
              <Spinner animation="border" />
            </div>
          ) : funds.length > 0 ? (
            <>
              <Table aria-busy={loading} responsive hover className="directory-table mb-0">
                <thead>
                  <tr>
                    <th>Fund Name</th>
                    <th>AMFI Code</th>
                    <th>ISIN</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {funds.map((fund) => (
                    <tr key={fund.id}>
                      <td className="directory-table__name">
                        <div>
                          <strong>
                            <Link to={`/mutual-funds/${fund.id}`}>
                              {fund.title}
                            </Link>
                          </strong>
                          {fund.body && (
                            <>
                              <br />
                              <small className="text-muted">{fund.body.substring(0, 100)}...</small>
                            </>
                          )}
                        </div>
                      </td>
                      <td>{fund.stockid}</td>
                      <td>
                        <code>{fund.isin || 'N/A'}</code>
                      </td>
                      <td>
                        <Button
                          size="sm"
                          variant="outline-primary"
                          onClick={() => handleAddToPortfolio(fund)}
                        >
                          Add to Portfolio
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="d-flex justify-content-center p-3">
                  <Pagination>
                    <Pagination.Prev 
                      disabled={currentPage === 1}
                      onClick={() => handlePageChange(currentPage - 1)}
                    />
                    
                    {[...Array(Math.min(5, totalPages))].map((_, index) => {
                      const page = currentPage - 2 + index;
                      if (page < 1 || page > totalPages) return null;
                      
                      return (
                        <Pagination.Item
                          key={page}
                          active={page === currentPage}
                          onClick={() => handlePageChange(page)}
                        >
                          {page}
                        </Pagination.Item>
                      );
                    })}
                    
                    <Pagination.Next 
                      disabled={currentPage === totalPages}
                      onClick={() => handlePageChange(currentPage + 1)}
                    />
                  </Pagination>
                </div>
              )}
            </>
          ) : (
            <div className="text-center p-4 text-muted">
              {searchTerm ? 'No mutual funds found matching your search.' : 'No mutual funds available.'}
            </div>
          )}
        </Card.Body>
      </Card>

      <AddMutualFundToPortfolioModal
        show={Boolean(fundToAdd)}
        fund={fundToAdd}
        onHide={() => setFundToAdd(null)}
        onSuccess={(addedFund) => setAddSuccess(`${addedFund.title} was added to your portfolio.`)}
      />
    </div>
  );
};

export default MutualFunds;
